// Mapping des réseaux sociaux vers les URLs d'icônes en ligne
// Utilise Simple Icons CDN qui supporte les couleurs personnalisées et différents formats

/**
 * Mapping des réseaux sociaux vers leurs slugs Simple Icons
 * Source: https://simpleicons.org/
 */
export const SOCIAL_NETWORKS_MAPPING = {
  facebook: 'facebook',
  twitter: 'x', // Twitter est maintenant X
  linkedin: 'linkedin',
  instagram: 'instagram',
  youtube: 'youtube',
  github: 'github',
  tiktok: 'tiktok',
  snapchat: 'snapchat',
  pinterest: 'pinterest',
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  discord: 'discord',
  reddit: 'reddit',
  twitch: 'twitch',
  spotify: 'spotify',

  skype: 'skype',
  slack: 'slack',
  zoom: 'zoom',
  vimeo: 'vimeo',

  email: 'gmail', // Utilise Gmail comme icône pour email
  website: 'googlechrome', // Utilise Chrome comme icône pour site web
  phone: 'phonepe' // Utilise une icône de téléphone
};

/**
 * Couleurs par défaut des réseaux sociaux (codes hex sans #)
 */
export const NETWORK_DEFAULT_COLORS = {
  facebook: '1877F2',
  twitter: '000000', // X est noir
  linkedin: '0A66C2',
  instagram: 'E4405F',
  youtube: 'FF0000',
  github: '181717',
  tiktok: '000000',
  snapchat: 'FFFC00',
  pinterest: 'BD081C',
  whatsapp: '25D366',
  telegram: '26A5E4',
  discord: '5865F2',
  reddit: 'FF4500',
  twitch: '9146FF',
  spotify: '1DB954',

  skype: '00AFF0',
  slack: '4A154B',
  zoom: '2D8CFF',
  vimeo: '1AB7EA',

  email: 'EA4335', // Rouge Gmail
  website: '4285F4', // Bleu Chrome
  phone: '25D366' // Vert WhatsApp
};

/**
 * URLs d'icônes PNG publiques pour les signatures d'email
 * Ces URLs pointent vers des icônes PNG iOS Filled d'Icons8 compatibles avec les clients email
 */
export const PNG_ICONS_URLS = {
  linkedin: 'https://img.icons8.com/ios-filled/50/FFFFFF/linkedin.png',
  facebook: 'https://img.icons8.com/ios-filled/50/FFFFFF/facebook-new.png',
  twitter: 'https://img.icons8.com/ios-filled/50/FFFFFF/x.png',
  instagram: 'https://img.icons8.com/ios-filled/50/FFFFFF/instagram-new.png',
  youtube: 'https://img.icons8.com/ios-filled/50/FFFFFF/youtube-play.png',
  github: 'https://img.icons8.com/ios-filled/50/FFFFFF/github.png',
  tiktok: 'https://img.icons8.com/ios-filled/50/FFFFFF/tiktok.png',
  snapchat: 'https://img.icons8.com/ios-filled/50/FFFFFF/snapchat.png',
  pinterest: 'https://img.icons8.com/ios-filled/50/FFFFFF/pinterest.png',
  whatsapp: 'https://img.icons8.com/ios-filled/50/FFFFFF/whatsapp.png',
  telegram: 'https://img.icons8.com/ios-filled/50/FFFFFF/telegram-app.png',
  discord: 'https://img.icons8.com/ios-filled/50/FFFFFF/discord-logo.png',
  reddit: 'https://img.icons8.com/ios-filled/50/FFFFFF/reddit.png',
  twitch: 'https://img.icons8.com/ios-filled/50/FFFFFF/twitch.png',
  spotify: 'https://img.icons8.com/ios-filled/50/FFFFFF/spotify.png',
  skype: 'https://img.icons8.com/ios-filled/50/FFFFFF/skype.png',
  slack: 'https://img.icons8.com/ios-filled/50/FFFFFF/slack-new.png',
  zoom: 'https://img.icons8.com/ios-filled/50/FFFFFF/zoom.png',
  vimeo: 'https://img.icons8.com/ios-filled/50/FFFFFF/vimeo.png'
};

/**
 * Génère l'URL d'une icône de réseau social en PNG
 * @param {string} network - Le nom du réseau social
 * @param {Object} options - Options de personnalisation
 * @returns {string|null} URL de l'icône PNG ou null si non trouvée
 */
export function getOnlineIconUrl(network, options = {}) {
  const networkKey = network.toLowerCase();
  const iconUrl = PNG_ICONS_URLS[networkKey];
  
  if (!iconUrl) {
    console.warn(`Réseau social non supporté: ${network}`);
    return null;
  }

  // Retourner directement l'URL PNG publique
  return iconUrl;
}

/**
 * Génère l'HTML d'une icône de réseau social en ligne
 * @param {string} network - Le nom du réseau social
 * @param {string} url - L'URL du profil
 * @param {Object} options - Options de style
 * @param {number} options.size - Taille de l'icône (défaut: 32)
 * @param {string} options.shape - Forme de l'icône ('rond', 'arrondi', 'carre')
 * @param {string} options.color - Couleur de l'icône
 * @param {string} options.backgroundColor - Couleur de fond
 * @returns {string} HTML de l'icône
 */
export function generateOnlineIconHTML(network, url, options = {}) {
  const {
    size = 32,
    shape = 'rond',
    backgroundColor = '#0077B5'
  } = options;

  const networkData = SOCIAL_NETWORKS_MAPPING[network.toLowerCase()];
  if (!networkData) {
    console.warn(`Réseau social non supporté: ${network}`);
    return '';
  }

  const borderRadius = shape === 'rond' ? '50%' : shape === 'arrondi' ? '20%' : '0%';
  
  return `
    <a href="${url}" target="_blank" style="text-decoration: none; display: inline-block; margin-right: 8px;">
      <img src="${networkData.iconUrl}" 
           alt="${networkData.name}" 
           title="${networkData.name}"
           style="width: ${size}px; height: ${size}px; background-color: ${backgroundColor}; border-radius: ${borderRadius}; padding: 4px; vertical-align: middle;" />
    </a>
  `;
}

/**
  * Vérifie si un réseau social est supporté
  * @param {string} network - Le nom du réseau social
  * @returns {boolean} True si supporté
  */
 export function isNetworkSupported(network) {
   return network.toLowerCase() in SOCIAL_NETWORKS_MAPPING;
 }

 /**
  * Obtient la liste de tous les réseaux sociaux supportés
  * @returns {string[]} Liste des réseaux supportés
  */
 export function getSupportedNetworks() {
   return Object.keys(SOCIAL_NETWORKS_MAPPING);
 }